import React from "react";
import { useTranslation } from "react-i18next";
import { OrderGroupDetail } from "@/api/models/api-types";

interface OrderAddressSectionProps {
  orderGroupDetail?: OrderGroupDetail;
}

export const OrderAddressSection: React.FC<OrderAddressSectionProps> = ({
  orderGroupDetail,
}) => {
  const { t } = useTranslation();

  // Get addresses from orderGroupDetail only (Order type doesn't have address properties)
  const shippingAddress = orderGroupDetail?.shippingAddress;
  const billingAddress = orderGroupDetail?.billingAddress;

  if (!shippingAddress || !billingAddress) return null;

  // Check if billing address is same as shipping
  const isSameAddress =
    shippingAddress.addressLine1 === billingAddress.addressLine1 &&
    shippingAddress.city === billingAddress.city &&
    shippingAddress.postalCode === billingAddress.postalCode;

  return (
    <div className="space-y-0">
      {/* Delivery Address */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-0 px-4 md:px-6 py-4 md:py-6">
        <h3 className="text-sm font-semibold text-text-primary dark:text-text-primary">
          {t("orders.deliveryAddress")}
        </h3>
        <div className="flex flex-col gap-2 w-full md:w-auto md:text-right">
          <p className="text-sm font-semibold text-text-primary dark:text-text-primary">
            {shippingAddress.label}
          </p>
          {/* First Name & Last Name */}
          {(shippingAddress.firstName || shippingAddress.lastName) && (
            <p className="text-sm font-medium text-text-primary dark:text-text-primary">
              {shippingAddress.firstName} {shippingAddress.lastName}
            </p>
          )}
          <div className="text-sm text-text-primary dark:text-text-primary space-y-1">
            <p>{shippingAddress.addressLine1}</p>
            {shippingAddress.addressLine2 && (
              <p>{shippingAddress.addressLine2}</p>
            )}
            <p>
              {shippingAddress.city}, {shippingAddress.state}{" "}
              {shippingAddress.postalCode}
            </p>
            <p>{shippingAddress.country}</p>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="h-px bg-[#e3e8ef] dark:bg-gray-700" />

      {/* Billing Address */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-0 px-4 md:px-6 py-4 md:py-6">
        <h3 className="text-sm font-semibold text-text-primary dark:text-text-primary">
          {t("orders.billingAddress")}
        </h3>
        <div className="flex flex-col gap-2 w-full md:w-auto md:text-right">
          {isSameAddress ? (
            <>
              <p className="text-sm font-semibold text-text-primary dark:text-text-primary">
                {t("orders.sameBillingAddress")}
              </p>
              <div className="text-sm text-text-primary dark:text-text-primary">
                <p>{billingAddress.addressLine1}</p>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm font-semibold text-text-primary dark:text-text-primary">
                {billingAddress.label}
              </p>
              {/* First Name & Last Name */}
              {(billingAddress.firstName || billingAddress.lastName) && (
                <p className="text-sm font-medium text-text-primary dark:text-text-primary">
                  {billingAddress.firstName} {billingAddress.lastName}
                </p>
              )}
              <div className="text-sm text-text-primary dark:text-text-primary space-y-1">
                <p>{billingAddress.addressLine1}</p>
                {billingAddress.addressLine2 && (
                  <p>{billingAddress.addressLine2}</p>
                )}
                <p>
                  {billingAddress.city}, {billingAddress.state}{" "}
                  {billingAddress.postalCode}
                </p>
                <p>{billingAddress.country}</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
